import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import postService, { Post } from '../../services/postService';
import mediaService, { MediaFile } from '../../services/mediaService';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Loader2 } from 'lucide-react';
import AuthenticatedImage from '../media/AuthenticatedImage';
import { useClient } from '../../contexts/ClientContext';
import FloatingLabelInput from '../ui/FloatingLabelInput';

interface PostSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPostSelect: (post: Post) => void;
}

interface PostWithThumbnail extends Post {
  thumbnailUrl?: string;
}

const PostSelectorModal: React.FC<PostSelectorModalProps> = ({ isOpen, onClose, onPostSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { currentClient } = useClient();
  const [postsWithThumbnails, setPostsWithThumbnails] = useState<PostWithThumbnail[]>([]);

  const { data: postsData, isLoading, error } = useQuery({
    queryKey: ['allPosts', searchTerm],
    queryFn: () => postService.getPosts(1, 100, undefined, searchTerm),
  });

  useEffect(() => {
    if (postsData?.posts) {
      const fetchThumbnails = async () => {
        const posts = postsData.posts;
        const postsWithThumbnails = await Promise.all(
          posts.map(async (post) => {
            if (post.media_files && post.media_files.length > 0) {
              try {
                const mediaFile = await mediaService.getMediaFile(post.media_files[0], currentClient?.id);
                return { ...post, thumbnailUrl: mediaFile.thumbnail_url || mediaFile.file_url };
              } catch (error) {
                console.error(`Failed to fetch media file for post ${post.id}`, error);
                return { ...post, thumbnailUrl: undefined };
              }
            } else {
              return { ...post, thumbnailUrl: undefined };
            }
          })
        );
        setPostsWithThumbnails(postsWithThumbnails);
      };
      fetchThumbnails();
    }
  }, [postsData, currentClient]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 w-full max-w-[800px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-black dark:text-white">Select a Post to Schedule</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <div className="mb-2">
            <FloatingLabelInput
              id="search-posts-modal"
              label="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {isLoading && <Loader2 className="mx-auto h-8 w-8 animate-spin text-gray-500" />}
            {error && <p className="text-red-500">Error loading posts.</p>}
            {postsWithThumbnails.map(post => (
              <div key={post.id} className="relative group cursor-pointer aspect-w-1 aspect-h-1 bg-gray-100 dark:bg-gray-900 rounded-lg border border-gray-300 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors" onClick={() => onPostSelect(post)}>
                {post.thumbnailUrl ? (
                  <AuthenticatedImage
                    src={post.thumbnailUrl}
                    alt={post.title}
                    className="w-full h-full object-cover rounded-lg"
                    clientId={currentClient?.id}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    <p className="text-gray-600 dark:text-gray-400 text-center p-2">{post.title}</p>
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                  <p className="text-white text-center p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">{post.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white dark:bg-gray-800 border-black dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-black dark:text-gray-300">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostSelectorModal;